import { useState, useCallback } from 'react'
import { devLog } from '../services/devLog'

interface UseOfficeDocumentResult {
  /** Read the current Word document as a Blob */
  getDocumentBlob: () => Promise<Blob>
  /** Insert a "Signed via KobSign" footer into the document */
  insertSignedFooter: (text: string) => Promise<void>
  /** Whether the document is being read */
  isReading: boolean
  /** Read error message */
  error: string | null
}

/**
 * Hook to read/write the current Word document using Office.js.
 *
 * Read: Uses Office.FileType.Compressed to get the DOCX binary data.
 * Write: Uses Word.run() to insert footer text.
 */
export function useOfficeDocument(): UseOfficeDocumentResult {
  const [isReading, setIsReading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getDocumentBlob = useCallback(async (): Promise<Blob> => {
    setIsReading(true)
    setError(null)

    try {
      devLog.info('Reading DOCX', 'Office.context.document.getFileAsync(Compressed)')
      return await new Promise<Blob>((resolve, reject) => {
        Office.context.document.getFileAsync(
          Office.FileType.Compressed,
          { sliceSize: 65536 }, // 64KB slices
          async (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              reject(new Error(result.error.message))
              return
            }

            const file = result.value
            const sliceCount = file.sliceCount
            const slices: ArrayBuffer[] = []

            try {
              for (let i = 0; i < sliceCount; i++) {
                const sliceData = await new Promise<ArrayBuffer>(
                  (resolveSlice, rejectSlice) => {
                    file.getSliceAsync(i, (sliceResult) => {
                      if (sliceResult.status === Office.AsyncResultStatus.Failed) {
                        rejectSlice(new Error(sliceResult.error.message))
                      } else {
                        // Office.js returns data as number[] — convert to ArrayBuffer
                        const data = sliceResult.value.data as unknown as number[]
                        const buffer = new Uint8Array(data).buffer as ArrayBuffer
                        resolveSlice(buffer)
                      }
                    })
                  },
                )
                slices.push(sliceData)
              }

              file.closeAsync()

              const blob = new Blob(slices, {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              })
              devLog.info('DOCX read OK', `${sliceCount} slices, ${blob.size} bytes`)
              resolve(blob)
            } catch (err) {
              file.closeAsync()
              reject(err)
            }
          },
        )
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'office.readError'
      devLog.error('DOCX read failed', message)
      setError(message)
      throw err
    } finally {
      setIsReading(false)
    }
  }, [])

  /**
   * Insert a footer into the current Word document.
   * Uses the Word JS API (Word.run) to access the document body's sections.
   */
  const insertSignedFooter = useCallback(async (text: string): Promise<void> => {
    setError(null)

    try {
      await Word.run(async (context) => {
        const sections = context.document.sections
        sections.load('items')
        await context.sync()

        // Insert footer in the first (primary) section
        const footer = sections.items[0].getFooter(Word.HeaderFooterType.primary)
        footer.load('text')
        await context.sync()

        // Only insert if not already present
        if (!footer.text.includes('KobSign')) {
          const paragraph = footer.insertParagraph(text, Word.InsertLocation.end)
          paragraph.alignment = Word.Alignment.centered
          paragraph.font.size = 8
          paragraph.font.color = '#999999'
          paragraph.font.italic = true
          await context.sync()
        }
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to insert footer'
      setError(message)
      throw err
    }
  }, [])

  return { getDocumentBlob, insertSignedFooter, isReading, error }
}
